<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      isDesktopWorkspace: false,
      desktopListWidth: 300,
      desktopSidebarWidth: 520,
      desktopResize: null,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      if (this.isDesktopWorkspace) {
        return true;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
    desktopListStyle() {
      if (!this.isDesktopWorkspace || this.isOnExpandedLayout) return {};
      return {
        width: `${this.desktopListWidth}px`,
        minWidth: `${this.desktopListWidth}px`,
        flexBasis: `${this.desktopListWidth}px`,
      };
    },
    desktopSidebarStyle() {
      if (!this.isDesktopWorkspace || !this.shouldShowSidebar) return {};
      return {
        width: `${this.desktopSidebarWidth}px`,
        minWidth: `${this.desktopSidebarWidth}px`,
        flexBasis: `${this.desktopSidebarWidth}px`,
        maxWidth: 'none',
      };
    },
    desktopWorkspaceStyle() {
      if (!this.isDesktopWorkspace) return {};
      return {
        '--mm-desktop-sidebar-width': `${this.desktopSidebarWidth}px`,
      };
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.restoreDesktopWorkspace();
    window.addEventListener('resize', this.updateDesktopWorkspace);
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    this.$watch('conversationId', () => this.openDesktopMiMarciana());
    this.$watch('currentChat.id', () => this.openDesktopMiMarciana());
    this.openDesktopMiMarciana();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateDesktopWorkspace);
    this.stopDesktopResize();
  },

  methods: {
    desktopWorkspaceWidth() {
      return this.$el?.clientWidth || window.innerWidth;
    },
    restoreDesktopWorkspace() {
      const savedList = Number(
        window.localStorage.getItem('mm-chatwoot:desktop-list-width')
      );
      const savedSidebar = Number(
        window.localStorage.getItem('mm-chatwoot:desktop-sidebar-width')
      );
      if (savedList >= 220 && savedList <= 520) {
        this.desktopListWidth = savedList;
      }
      if (savedSidebar >= 360 && savedSidebar <= 900) {
        this.desktopSidebarWidth = savedSidebar;
      }
      this.updateDesktopWorkspace();
    },
    updateDesktopWorkspace() {
      this.isDesktopWorkspace = window.innerWidth >= 1024;
      if (!this.isDesktopWorkspace) {
        this.stopDesktopResize();
        return;
      }
      const workspaceWidth = this.desktopWorkspaceWidth();
      this.desktopListWidth = Math.min(
        this.desktopListWidth,
        Math.max(220, workspaceWidth - 820)
      );
      this.desktopSidebarWidth = Math.min(
        this.desktopSidebarWidth,
        Math.max(360, workspaceWidth - this.desktopListWidth - 434)
      );
      this.openDesktopMiMarciana();
    },
    openDesktopMiMarciana() {
      if (!this.isDesktopWorkspace || !this.currentChat.id) return;
      if (!this.uiSettings.is_contact_sidebar_open) {
        this.updateUISettings({
          is_contact_sidebar_open: true,
          is_copilot_panel_open: false,
        });
      }
    },
    startDesktopResize(kind, event) {
      if (!this.isDesktopWorkspace) return;
      event.preventDefault();
      this.desktopResize = {
        kind,
        startX: event.clientX,
        startListWidth: this.desktopListWidth,
        startSidebarWidth: this.desktopSidebarWidth,
      };
      document.body.classList.add('mm-desktop-resizing');
      window.addEventListener('pointermove', this.moveDesktopResize);
      window.addEventListener('pointerup', this.stopDesktopResize);
    },
    moveDesktopResize(event) {
      if (!this.desktopResize) return;
      const delta = event.clientX - this.desktopResize.startX;
      const workspaceWidth = this.desktopWorkspaceWidth();
      if (this.desktopResize.kind === 'list') {
        const maximum = Math.max(
          220,
          workspaceWidth - this.desktopSidebarWidth - 434
        );
        this.desktopListWidth = Math.min(
          maximum,
          Math.max(220, this.desktopResize.startListWidth + delta)
        );
      } else {
        const maximum = Math.max(
          360,
          workspaceWidth - this.desktopListWidth - 434
        );
        this.desktopSidebarWidth = Math.min(
          maximum,
          Math.max(360, this.desktopResize.startSidebarWidth - delta)
        );
      }
    },
    stopDesktopResize() {
      if (!this.desktopResize) return;
      window.localStorage.setItem(
        'mm-chatwoot:desktop-list-width',
        String(Math.round(this.desktopListWidth))
      );
      window.localStorage.setItem(
        'mm-chatwoot:desktop-sidebar-width',
        String(Math.round(this.desktopSidebarWidth))
      );
      this.desktopResize = null;
      document.body.classList.remove('mm-desktop-resizing');
      window.removeEventListener('pointermove', this.moveDesktopResize);
      window.removeEventListener('pointerup', this.stopDesktopResize);
    },
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        const scopedInboxId = Number(this.inboxId) || 0;
        const selectedInboxId = Number(selectedConversation?.inbox_id) || 0;

        // A conversation selected in another inbox must never remain visible
        // when the agent opens a scoped channel. This is especially important
        // for the independent public Instagram comments inbox: comments and
        // private messaging are deliberately separate surfaces.
        if (
          selectedConversation &&
          scopedInboxId &&
          selectedInboxId !== scopedInboxId
        ) {
          this.$store.dispatch('clearSelectedState');
          this.$router.replace({
            name: 'inbox_dashboard',
            params: {
              accountId: this.accountId,
              inbox_id: scopedInboxId,
            },
          });
          return;
        }
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section
    class="flex h-[100dvh] max-h-[100dvh] min-h-0 w-full min-w-0 overflow-hidden"
    :style="desktopWorkspaceStyle"
  >
    <ChatList
      :style="desktopListStyle"
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <div
      v-if="isDesktopWorkspace && showConversationList && !isOnExpandedLayout"
      class="mm-desktop-splitter"
      role="separator"
      :aria-label="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @pointerdown="startDesktopResize('list', $event)"
    />
    <ConversationBox
      v-if="showMessageView && conversationId"
      :class="isDesktopWorkspace ? 'min-w-0 flex-1 !w-auto' : ''"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <div
      v-if="isDesktopWorkspace && shouldShowSidebar"
      class="mm-desktop-splitter"
      role="separator"
      :aria-label="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @pointerdown="startDesktopResize('sidebar', $event)"
    />
    <!--
      En móvil, cerrar el panel para volver al chat no debe desmontar Mi
      Marciana Chat. v-show la oculta conservando vivo el iframe y, con él,
      la pantalla exacta: producto, pedido, clienta, formulario, modal y
      posición de desplazamiento. Solo se crea cuando existe conversación.
    -->
    <ConversationSidebar
      v-if="currentChat.id"
      v-show="shouldShowSidebar"
      :style="desktopSidebarStyle"
      :current-chat="currentChat"
    />
    <CmdBarConversationSnooze />
  </section>
</template>

<style>
@media (min-width: 1024px) {
  .mm-desktop-splitter {
    position: relative;
    z-index: 30;
    width: 7px;
    min-width: 7px;
    height: 100%;
    cursor: col-resize;
    touch-action: none;
    background: transparent;
  }

  .mm-desktop-splitter::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 3px;
    width: 1px;
    background: rgb(228 228 231);
    transition:
      width 120ms ease,
      left 120ms ease,
      background 120ms ease;
  }

  .mm-desktop-splitter:hover::after,
  .mm-desktop-resizing .mm-desktop-splitter::after {
    left: 2px;
    width: 3px;
    background: #f33f8f;
  }

  .mm-desktop-resizing,
  .mm-desktop-resizing * {
    cursor: col-resize !important;
    user-select: none !important;
  }
}
</style>
